import {HttpClient} from '@angular/common/http';
import {Injectable, computed, inject, signal} from '@angular/core';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import {environment} from '../../../environments/environment';
import {parseApiError} from '../api/api-error.util';
import {
  ApiAvailableChannel,
  ApiBankAccount,
  ApiCategory,
  ApiDashboard,
  ApiEnvelope,
  ApiNewspaperPartner,
  ApiNotification,
  ApiPaginated,
  ApiPaymentEntry,
  ApiPublication,
  ApiPublicationDocument,
  ApiPublicationQuery,
  ApiPublicationQuote,
  ApiTransactionSummary,
  ApiWalletLedger,
  ApiWalletSummary,
} from '../api/api.types';
import {isPaymentConfirmed} from '../api/payment-verification.util';
import {
  CHANNEL_DEFAULT_FEES,
  channelLabelFromType,
  mapBankAccount,
  mapCategory,
  mapDashboard,
  mapNewspaper,
  mapNotification,
  mapPublication,
  mapPublicationQuote,
  mapTransaction,
  mapWallet,
} from '../api/portal.mappers';
import {isApplicantUser} from '../guards/role.guard';
import {
  AvailableChannel,
  Bank,
  BankAccount,
  Category,
  DashboardData,
  Newspaper,
  Notification,
  Publication,
  PublicationQuote,
  PublicationStatus,
  ServerDocument,
  Transaction,
  UploadedDocument,
  WalletData,
  WizardState,
} from '../models/models';
import {AuthService} from './auth.service';

/**
 * Loads all portal data.
 *
 * Authenticated users: publications, categories, newspapers, dashboard, wallet,
 * transactions and notifications come from npr_api.
 */
@Injectable({ providedIn: 'root' })
export class PortalDataService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly staticBase = '/data';
  private readonly api = environment.apiUrl;

  readonly publications = signal<Publication[]>([]);
  readonly categories = signal<Category[]>([]);
  readonly newspapers = signal<Newspaper[]>([]);
  readonly notifications = signal<Notification[]>([]);
  readonly transactions = signal<Transaction[]>([]);
  readonly dashboard = signal<DashboardData | null>(null);
  readonly wallet = signal<WalletData | null>(null);
  readonly transactionSummary = signal<{
    totalSpent: number;
    spendingLabels: string[];
    spendingData: number[];
  } | null>(null);
  readonly loaded = signal(false);
  readonly live = signal(false);
  /** From GET /portal/notifications/unread-count when available. */
  readonly serverUnreadCount = signal<number | null>(null);

  readonly unreadCount = computed(
    () => this.serverUnreadCount() ?? this.notifications().filter((n) => !n.read).length
  );
  readonly drafts = computed(() =>
    this.publications().filter((p) => p.status === 'Draft')
  );
  readonly walletBalance = computed(() => this.wallet()?.balance ?? 0);

  constructor() {
    this.auth.ensureSession().subscribe(() => this.load());
  }

  recentPublications(count = 5): Publication[] {
    return this.publications().slice(0, count);
  }

  countByStatus(status: PublicationStatus): number {
    return this.publications().filter((p) => p.status === status).length;
  }

  /**
   * Re-fetch the publications list (and the dashboard counts derived from
   * it) after a save/submit succeeds.
   *
   * `PortalDataService` is a root singleton, so `load()`/`loadLive()` only
   * ever runs once, at app boot. Without this, a newly created or updated
   * publication is invisible on My Publications / Drafts / the dashboard --
   * they all read the `publications` signal, which nothing else here ever
   * updates -- until the user happens to fully reload the page, which
   * re-runs the constructor and fetches a fresh list.
   */
  refreshPublications(): void {
    const publications$ = this.http
      .get<ApiPaginated<ApiPublication>>(
        `${this.api}/portal/publications?per_page=100`
      )
      .pipe(catchError((err) => this.logFail('publications', err, { data: [] })));

    const dashboard$ = this.http
      .get<ApiEnvelope<ApiDashboard>>(`${this.api}/portal/dashboard`)
      .pipe(
        catchError((err) =>
          this.logFail('dashboard', err, { data: null as unknown as ApiDashboard })
        )
      );

    forkJoin({ publications: publications$, dashboard: dashboard$ }).subscribe(
      ({ publications, dashboard }) => {
        const pubs = (publications.data ?? []).map(mapPublication);
        this.publications.set(pubs);
        this.dashboard.set(
          mapDashboard(
            dashboard.data ?? {
              counts: { drafts: 0, submitted: 0, processing: 0, completed: 0, archived: 0 },
              recent_publications: [],
            },
            pubs
          )
        );
      }
    );
  }



  deletePublication(id: string): void {
    this.http.delete(`${this.api}/portal/publications/${id}`).subscribe({
      next: () =>
        this.publications.update((list) => list.filter((p) => p.id !== id)),
      error: (err) => console.error('Failed to delete publication', err),
    });
  }

  /** GET /portal/publications/{id} — includes documents + channels. */
  getPublication(id: string): Observable<ApiPublication> {
    return this.http
      .get<ApiEnvelope<ApiPublication>>(`${this.api}/portal/publications/${id}`)
      .pipe(map((res) => res.data));
  }

  /**
   * Questions a reviewer has asked about this publication.
   *
   * An open query stops the work: the newspaper cannot go further until it is
   * answered. Until now the portal had no way to show one, let alone answer it.
   */
  getPublicationQueries(publicationId: string): Observable<ApiPublicationQuery[]> {
    return this.http
      .get<ApiEnvelope<ApiPublicationQuery[]>>(
        `${this.api}/portal/publications/${publicationId}/queries`
      )
      .pipe(
        map((res) => res.data ?? []),
        catchError((err) => {
          console.error('Failed to load queries', err);
          return of([] as ApiPublicationQuery[]);
        })
      );
  }

  /** Answer a query, optionally attaching whatever was asked for. */
  respondToQuery(
    publicationId: string,
    queryId: string,
    response: string,
    documents: File[] = []
  ): Observable<ApiPublicationQuery> {
    const form = new FormData();
    form.append('response', response);
    documents.forEach((file) => form.append('documents[]', file));

    return this.http
      .post<ApiEnvelope<ApiPublicationQuery>>(
        `${this.api}/portal/publications/${publicationId}/queries/${queryId}/respond`,
        form
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Public feed of APPROVED (completed) publications — no auth required.
   * The backend endpoint filters to status = completed.
   */
  getPublicPublications(perPage = 12): Observable<Publication[]> {
    return this.http
      .get<ApiPaginated<ApiPublication>>(
        `${this.api}/core/public/publications?per_page=${perPage}`
      )
      .pipe(
        map((res) => (res.data ?? []).map(mapPublication)),
        catchError((err) => {
          console.error('Failed to load public publications', err);
          return of([] as Publication[]);
        })
      );
  }

  /** Create a new draft. */
  createDraft(state: WizardState): Observable<{ publicationId: string }> {
    const applicant = state.applicantDetails ?? {};
    const content = { ...state.content };
    const metadata = {
      applicant,
      channels: state.channels,
      newspapers: state.selectedNewspapers.map((n) => ({
        id: n.id,
        name: n.name,
      })),
      newspaper: state.selectedNewspapers[0]?.name,
      title: this.deriveTitle(content, state),
    };

    return this.http
      .post<ApiEnvelope<ApiPublication>>(`${this.api}/portal/publications`, {
        category_id: state.category?.id,
        metadata,
        content,
      })
      .pipe(map((res) => ({ publicationId: String(res.data.id) })));
  }

  /** Update an existing draft. */
  updateDraft(state: WizardState): Observable<{ publicationId: string }> {
    const applicant = state.applicantDetails ?? {};
    const content = { ...state.content };
    const metadata = {
      applicant,
      channels: state.channels,
      newspapers: state.selectedNewspapers.map((n) => ({
        id: n.id,
        name: n.name,
      })),
      newspaper: state.selectedNewspapers[0]?.name,
      title: this.deriveTitle(content, state),
    };

    return this.http
      .put<ApiEnvelope<ApiPublication>>(
        `${this.api}/portal/publications/${state.publicationId}`,
        { metadata, content }
      )
      .pipe(map((res) => ({ publicationId: String(res.data.id) })));
  }



  /** GET /portal/publications/{id}/documents — authoritative list for skip/replace. */
  listPublicationDocuments(publicationId: string): Observable<ServerDocument[]> {
    return this.http
      .get<ApiEnvelope<ApiPublicationDocument[]> | { data: ApiPublicationDocument[] }>(
        `${this.api}/portal/publications/${publicationId}/documents`
      )
      .pipe(
        map((res) => {
          const list = Array.isArray((res as ApiEnvelope<ApiPublicationDocument[]>).data)
            ? (res as ApiEnvelope<ApiPublicationDocument[]>).data
            : [];
          return (list ?? []).map((d) => ({
            id: String(d.id),
            documentType: d.document_type,
            fileName: d.file_name ?? d.document_type,
          }));
        }),
        catchError((err) => {
          console.error('Failed to list publication documents', err);
          return of([] as ServerDocument[]);
        })
      );
  }

  /**
   * Hydrate wizard fields from a server draft (Continue Draft / ?id=).
   * Category schema comes from the already-loaded categories list when possible.
   */
  buildWizardPartialFromPublication(
    api: ApiPublication,
    categories: Category[],
    newspapers: Newspaper[]
  ): Partial<WizardState> {
    const categoryId = api.category?.id != null ? String(api.category.id) : null;
    const category =
      (categoryId && categories.find((c) => c.id === categoryId)) ||
      (api.category ? mapCategory(api.category, 0) : null);

    const meta = api.metadata ?? {};
    const applicant =
      meta['applicant'] && typeof meta['applicant'] === 'object'
        ? (meta['applicant'] as Record<string, unknown>)
        : null;

    const serverDocuments: ServerDocument[] = (api.documents ?? []).map((d) => ({
      id: String(d.id),
      documentType: d.document_type,
      fileName: d.file_name ?? d.document_type,
    }));

    const documents: UploadedDocument[] = (api.documents ?? []).map((d) =>
      this.mapApiDocMeta(d)
    );

    const channelLabels: string[] = [];
    const feeSummary: Record<string, number> = {};
    const selectedNewspapers: Newspaper[] = [];
    const seenPartners = new Set<string>();

    for (const ch of api.channels ?? []) {
      const type = ch.channel_type ?? ch.type ?? '';
      const label = channelLabelFromType(type);
      if (label && !channelLabels.includes(label)) {
        channelLabels.push(label);
        if (label !== 'Newspaper Publication') {
          feeSummary[label] = CHANNEL_DEFAULT_FEES[label] ?? 0;
        }
      }
      if (type === 'newspaper' && ch.partner_id != null) {
        const pid = String(ch.partner_id);
        if (seenPartners.has(pid)) continue;
        seenPartners.add(pid);
        const fromList = newspapers.find((n) => n.id === pid);
        if (fromList) {
          selectedNewspapers.push(fromList);
        } else if (ch.partner) {
          selectedNewspapers.push(mapNewspaper(ch.partner));
        }
      }
    }

    if (!channelLabels.length && Array.isArray(meta['channels'])) {
      for (const label of meta['channels'] as string[]) {
        if (typeof label === 'string' && label && !channelLabels.includes(label)) {
          channelLabels.push(label);
          if (label !== 'Newspaper Publication') {
            feeSummary[label] = CHANNEL_DEFAULT_FEES[label] ?? 0;
          }
        }
      }
    }

    if (!selectedNewspapers.length && Array.isArray(meta['newspapers'])) {
      for (const n of meta['newspapers'] as { id?: string | number; name?: string }[]) {
        const id = n?.id != null ? String(n.id) : '';
        const fromList = newspapers.find((x) => x.id === id);
        if (fromList) selectedNewspapers.push(fromList);
      }
    }

    if (selectedNewspapers.length) {
      feeSummary['Newspaper Publication'] = selectedNewspapers.reduce(
        (sum, n) => sum + (categoryId && n.categoryRates?.[categoryId] != null ? n.categoryRates[categoryId] : n.pricePerPublication),
        0
      );
    }

    return {
      publicationId: String(api.id),
      category,
      applicantDetails: applicant,
      content: { ...(api.current_version?.content ?? {}) },
      documents,
      serverDocuments,
      channels: channelLabels,
      selectedNewspapers,
      feeSummary,
      referenceNumber: null,
      pendingPaymentRefId: null,
      isPaid: !!api.quote?.is_paid,
    };
  }

  /**
   * Step 8 "Pay": prepare the publication then start Paystack checkout.
   */
  beginPublicationPayment(
    state: WizardState,
    files: Record<string, File>,
    onDraftReady?: (ids: {
      publicationId: string;
      pendingPaymentRefId?: string;
      serverDocuments?: ServerDocument[];
    }) => void
  ): Observable<{ publicationId: string; refId: string; authorizationUrl: string; accessCode: string | null }> {
    const email = this.resolveCheckoutEmail(state.applicantDetails ?? {});
    return this.preparePublicationForPayment(state, files, onDraftReady).pipe(
      switchMap(({ publicationId, refId }) =>
        this.initiatePaystack(refId, email).pipe(
          map(({ authorizationUrl, accessCode }) => ({
            publicationId,
            refId,
            authorizationUrl,
            accessCode,
          }))
        )
      )
    );
  }

  /**
   * Step 8 "Pay with wallet": upsert draft (no Paystack entry), debit wallet.
   *
   * Stops at payment -- submission is a separate, explicit action the wizard
   * asks for afterwards (see PublicationWizardComponent.submitPaidPublication),
   * not a side effect of paying.
   */
  beginWalletPublicationPayment(
    state: WizardState,
    files: Record<string, File>,
    onDraftReady?: (ids: {
      publicationId: string;
      pendingPaymentRefId?: string;
      serverDocuments?: ServerDocument[];
    }) => void
  ): Observable<{ publicationId: string }> {
    return this.preparePublicationForPayment(state, files, onDraftReady, {
      createPaymentEntry: false,
    }).pipe(
      switchMap(({ publicationId }) =>
        this.payPublicationWithWallet(publicationId).pipe(
          tap(() => this.refreshWallet()),
          map(() => ({ publicationId }))
        )
      )
    );
  }

  /**
   * Channels the platform currently offers, priced.
   *
   * The server omits any channel switched off in configuration and returns the
   * real base price and per-channel service charge, so the wizard neither shows
   * an undeliverable channel nor carries its own price table.
   */
  publicationChannels(categoryId: number | string | null): Observable<AvailableChannel[]> {
    const params = categoryId ? `?category_id=${categoryId}` : '';
    return this.http
      .get<ApiEnvelope<ApiAvailableChannel[]>>(
        `${this.api}/portal/publications/channels${params}`
      )
      .pipe(
        map((res) =>
          (res.data ?? []).map((c) => ({
            type: c.type,
            name: c.name,
            basePrice: Number(c.base_price ?? 0) || 0,
            serviceCharge: Number(c.service_charge ?? 0) || 0,
            vat: Number(c.vat ?? 0) || 0,
            totalPrice: Number(c.total_price ?? c.base_price ?? 0) || 0,
          }))
        )
      );
  }

  /**
   * Ask the server to price a channel selection before the draft exists.
   *
   * The wizard used to compute this in the browser, which meant the price on
   * screen and the price charged were produced by two different
   * implementations and could disagree. This routes the estimate through the
   * same pricing engine that will charge the applicant.
   */
  previewQuote(
    categoryId: number | string | null,
    channels: { channel_type: string; partner_id?: number | null }[]
  ): Observable<PublicationQuote> {
    return this.http
      .post<ApiEnvelope<ApiPublicationQuote>>(
        `${this.api}/portal/publications/quote-preview`,
        { category_id: categoryId ?? null, channels }
      )
      .pipe(map((res) => mapPublicationQuote(res.data)));
  }

  /**
   * Price the wizard's current selection. Uses the same channel payload the
   * draft will be created with, so the preview cannot describe a different
   * basket than the one that gets charged.
   */
  previewQuoteForState(state: WizardState): Observable<PublicationQuote> {
    const channels = this.buildChannelPayload(state);

    if (!channels.length) {
      return of({
        quoteId: null,
        subtotal: 0,
        serviceCharge: 0,
        vat: 0,
        totalAmount: 0,
        currency: 'NGN',
        isPreview: true,
        expiresAt: null,
        lines: [],
      } satisfies PublicationQuote);
    }

    return this.previewQuote(
      state.category?.id ? Number(state.category.id) : null,
      channels
    );
  }

  /**
   * The frozen quote for a stored draft. This is the payable figure.
   *
   * `refresh` re-prices from current rules and supersedes the stored quote;
   * without it an existing unexpired quote is returned unchanged so the price
   * does not move under the applicant.
   */
  publicationQuote(publicationId: string, refresh = false): Observable<PublicationQuote> {
    const suffix = refresh ? '?refresh=1' : '';
    return this.http
      .get<ApiEnvelope<ApiPublicationQuote>>(
        `${this.api}/portal/publications/${publicationId}/payment-summary${suffix}`
      )
      .pipe(map((res) => mapPublicationQuote(res.data)));
  }

  /** Debit wallet for a prepared publication draft. */
  payPublicationWithWallet(publicationId: string): Observable<void> {
    return this.http
      .post<{ success?: boolean; message?: string }>(
        `${this.api}/portal/publications/${publicationId}/pay-with-wallet`,
        {}
      )
      .pipe(map(() => void 0));
  }

  /**
   * Upsert draft + docs + channels (+ optional payment entry for Paystack).
   * Calls onDraftReady as soon as the publication id is known so retries use PUT.
   */
  preparePublicationForPayment(
    state: WizardState,
    files: Record<string, File>,
    onDraftReady?: (ids: {
      publicationId: string;
      pendingPaymentRefId?: string;
      serverDocuments?: ServerDocument[];
    }) => void,
    options: { createPaymentEntry?: boolean } = {}
  ): Observable<{ publicationId: string; refId: string }> {
    const createPaymentEntry = options.createPaymentEntry !== false;
    const applicant = state.applicantDetails ?? {};
    const payeeEmail = this.resolveCheckoutEmail(applicant);
    if (createPaymentEntry && !payeeEmail) {
      return throwError(
        () =>
          new Error(
            'A valid email is required for card payment. Update your profile email or enter a valid applicant email in Step 2.'
          )
      );
    }

    const publicationId = state.publicationId;
    if (!publicationId) {
       return throwError(() => new Error('Cannot start payment without a saved draft.'));
    }

    if (!createPaymentEntry) {
      return of({ publicationId, refId: '' });
    }

    if (state.pendingPaymentRefId) {
      onDraftReady?.({
        publicationId,
        pendingPaymentRefId: state.pendingPaymentRefId,
      });
      return of({
        publicationId,
        refId: state.pendingPaymentRefId,
      });
    }

    return this.http
      .post<ApiEnvelope<ApiPaymentEntry>>(
        `${this.api}/portal/publications/${publicationId}/pay`,
        {}
      )
      .pipe(
        map((payRes) => {
          const refId = payRes.data?.ref_id;
          if (!refId) {
            throw new Error('Could not start payment (no reference).');
          }
          onDraftReady?.({ publicationId, pendingPaymentRefId: refId });
          return { publicationId, refId };
        })
      );
  }

  private initiatePaystack(
    refId: string,
    email: string | null
  ): Observable<{ authorizationUrl: string; accessCode: string | null }> {
    if (!email) {
      return throwError(
        () =>
          new Error(
            'A valid email is required for card payment. Update your profile email or enter a valid applicant email in Step 2.'
          )
      );
    }

    return this.http
      .post<{
        authorization_url?: string;
        access_code?: string;
        error?: string;
        message?: string;
      }>(`${this.api}/payment/paystack/initiate-payment`, {
        reference: refId,
        email,
        client_app: 'portal',
      })
      .pipe(
        map((init) => {
          if (!init.authorization_url) {
            throw new Error(
              init.error ||
                init.message ||
                'Could not get a Paystack checkout URL.'
            );
          }
          return { authorizationUrl: init.authorization_url, accessCode: init.access_code ?? null };
        })
      );
  }

  verifyPayment(refId: string): Observable<{ success: boolean; raw: unknown }> {
    return this.http
      .post<{ status?: string }>(
        `${this.api}/payment/paystack/verify-and-log-payment`,
        { reference: refId }
      )
      .pipe(
        map((res) => ({
          success: isPaymentConfirmed(res),
          raw: res,
        })),
        catchError((err) => {
          console.error('Payment verification failed', err);
          return of({ success: false, raw: err });
        })
      );
  }

  /**
   * Check a publication is complete enough to pay for -- category fields,
   * required documents, and channel selection -- before Review lets the
   * applicant proceed to Payment. The same check `submit` runs internally
   * afterwards; this just surfaces it earlier, while it's still cheap to fix.
   */
  validatePublication(
    publicationId: string,
    acceptedDisclaimer: boolean
  ): Observable<{
    is_valid: boolean;
    errors: Record<string, string[]>;
    missing_documents: { document_type: string; label: string }[];
  }> {
    return this.http
      .post<{
        data: {
          is_valid: boolean;
          errors: Record<string, string[]>;
          missing_documents: { document_type: string; label: string }[];
        };
      }>(`${this.api}/portal/publications/${publicationId}/validate`, {
        accepted_disclaimer: acceptedDisclaimer,
      })
      .pipe(map((res) => res.data));
  }

  submitPublication(publicationId: string): Observable<{ reference: string }> {
    return this.http
      .post<ApiEnvelope<ApiPublication>>(
        `${this.api}/portal/publications/${publicationId}/submit`,
        {}
      )
      .pipe(
        tap((res) => {
          if (res.data) {
            this.publications.update((list) => [mapPublication(res.data), ...list]);
          }
        }),
        map((res) => ({ reference: res.data?.reference ?? '' }))
      );
  }

  private mapApiDocMeta(d: ApiPublicationDocument): UploadedDocument {
    const sizeKb =
      typeof d.size === 'number' ? Math.max(1, Math.round(d.size / 1024)) : null;
    return {
      documentType: d.document_type,
      fileName: d.file_name ?? d.document_type,
      size: sizeKb != null ? `${sizeKb} KB` : 'On file',
    };
  }

  uploadDocument(
    publicationId: string,
    file: File,
    documentType: string,
    required: boolean,
    label: string
  ): Observable<unknown> {
    const form = new FormData();
    form.append('file', file);
    form.append('document_type', documentType);
    return this.http
      .post(`${this.api}/portal/publications/${publicationId}/documents`, form)
      .pipe(
        catchError((err) => {
          console.error('Document upload failed', documentType, file.name, err);
          if (required) {
            const detail = parseApiError(err, '');
            return throwError(
              () =>
                new Error(
                  `Couldn't upload "${label}"${detail ? ': ' + detail : '.'}`
                )
            );
          }
          return of(null);
        })
      );
  }

  replaceDocument(
    publicationId: string,
    documentId: string,
    file: File,
    required: boolean,
    label: string
  ): Observable<unknown> {
    // Sent as POST with a spoofed method, not a genuine PUT: PHP only parses
    // multipart/form-data bodies into $_FILES on POST, so a real PUT here
    // silently drops the file and the API sees "the file field is required".
    const form = new FormData();
    form.append('file', file);
    form.append('_method', 'PUT');
    return this.http
      .post(
        `${this.api}/portal/publications/${publicationId}/documents/${documentId}`,
        form
      )
      .pipe(
        catchError((err) => {
          console.error('Document replace failed', documentId, file.name, err);
          if (required) {
            const detail = parseApiError(err, '');
            return throwError(
              () =>
                new Error(
                  `Couldn't replace "${label}"${detail ? ': ' + detail : '.'}`
                )
            );
          }
          return of(null);
        })
      );
  }

  /**
   * Email Paystack will charge against (auth user first, then wizard applicant).
   */
  private resolveCheckoutEmail(
    applicant: Record<string, unknown>
  ): string | null {
    const candidates = [
      this.auth.user()?.email,
      typeof applicant['email'] === 'string' ? applicant['email'] : null,
    ];
    for (const raw of candidates) {
      const email = typeof raw === 'string' ? raw.trim() : '';
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return email;
      }
    }
    return null;
  }

  private deriveTitle(content: Record<string, unknown>, state: WizardState): string {
    const candidate =
      content['title'] ??
      content['new_name'] ??
      content['former_name'] ??
      Object.values(content).find((v) => typeof v === 'string' && v.trim());
    return (candidate as string) || `${state.category?.name ?? 'Publication'} request`;
  }

  setChannels(
    publicationId: string,
    state: WizardState
  ): Observable<unknown> {
    const channels = this.buildChannelPayload(state);
    if (!channels.length) return of(null);
    return this.http
      .post(`${this.api}/portal/publications/${publicationId}/channels`, {
        channels,
      })
      .pipe(
        catchError((err) => {
          console.error('Setting channels failed (non-fatal)', err);
          return of(null);
        })
      );
  }

  private buildChannelPayload(
    state: WizardState
  ): { channel_type: string; partner_id?: number }[] {
    const out: { channel_type: string; partner_id?: number }[] = [];
    if (state.channels.includes('News and Publication Registry')) {
      out.push({ channel_type: 'platform' });
    }
    // Derived from the papers themselves rather than a channel toggle: the
    // newspaper channel is no longer selectable on the channels step, so the
    // presence of a selected paper is what says "publish in print".
    for (const np of state.selectedNewspapers) {
      out.push({ channel_type: 'newspaper', partner_id: Number(np.id) });
    }
    if (state.channels.includes('Email Notification')) {
      out.push({ channel_type: 'email' });
    }
    if (state.channels.includes('Official Gazette')) {
      out.push({ channel_type: 'digital_gazette' });
    }
    return out;
  }

  private load(): void {
    const user = this.auth.user();
    if (user && isApplicantUser(user)) {
      this.loadLive();
    } else {
      this.loadStatic();
    }
  }

  private loadLive(): void {
    const publications$ = this.http
      .get<ApiPaginated<ApiPublication>>(
        `${this.api}/portal/publications?per_page=100`
      )
      .pipe(catchError((err) => this.logFail('publications', err, { data: [] })));

    const categories$ = this.http
      .get<ApiPaginated<ApiCategory> | ApiCategory[]>(
        `${this.api}/portal/publications/categories`
      )
      .pipe(catchError((err) => this.logFail('categories', err, [])));

    const newspapers$ = this.http
      .get<ApiPaginated<ApiNewspaperPartner> | ApiNewspaperPartner[]>(
        `${this.api}/portal/publications/newspapers`
      )
      .pipe(catchError((err) => this.logFail('newspapers', err, [])));

    const dashboard$ = this.http
      .get<ApiEnvelope<ApiDashboard>>(`${this.api}/portal/dashboard`)
      .pipe(
        catchError((err) =>
          this.logFail('dashboard', err, { data: null as unknown as ApiDashboard })
        )
      );

    forkJoin({
      publications: publications$,
      categories: categories$,
      newspapers: newspapers$,
      dashboard: dashboard$,
    })
      .pipe(
        map(({ publications, categories, newspapers, dashboard }) => {
          const pubs = (publications.data ?? []).map(mapPublication);
          const categoryList = Array.isArray(categories)
            ? categories
            : (categories.data ?? []);
          const newspaperList = Array.isArray(newspapers)
            ? newspapers
            : (newspapers.data ?? []);
          return {
            publications: pubs,
            categories: categoryList.map(mapCategory),
            newspapers: newspaperList.map(mapNewspaper),
            dashboard: mapDashboard(
              dashboard.data ?? {
                counts: {
                  drafts: 0,
                  submitted: 0,
                  processing: 0,
                  completed: 0,
                  archived: 0,
                },
                recent_publications: [],
              },
              pubs
            ),
          };
        })
      )
      .subscribe((core) => {
        this.publications.set(core.publications);
        this.categories.set(core.categories);
        this.newspapers.set(core.newspapers);
        this.dashboard.set(core.dashboard);
        this.live.set(true);
        this.loadSupplemental();
        this.loaded.set(true);
      });
  }

  private logFail<T>(label: string, err: unknown, fallback: T): Observable<T> {
    console.error(`Failed to load ${label} from portal API`, err);
    return of(fallback);
  }

  private loadStatic(): void {
    forkJoin({
      publications: this.http.get<Publication[]>(`${this.staticBase}/publications.json`),
      categories: this.http.get<Category[]>(`${this.staticBase}/categories.json`),
      newspapers: this.http.get<Newspaper[]>(`${this.staticBase}/newspapers.json`),
      notifications: this.http.get<Notification[]>(`${this.staticBase}/notifications.json`),
      transactions: this.http.get<Transaction[]>(`${this.staticBase}/transactions.json`),
      dashboard: this.http.get<DashboardData>(`${this.staticBase}/dashboard.json`),
      wallet: this.http.get<WalletData>(`${this.staticBase}/wallet.json`),
    })
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        if (data) {
          this.publications.set(data.publications);
          this.categories.set(data.categories);
          this.newspapers.set(data.newspapers);
          this.notifications.set(data.notifications);
          this.transactions.set(data.transactions);
          this.dashboard.set(data.dashboard);
          this.wallet.set(data.wallet);
        }
        this.live.set(false);
        this.loaded.set(true);
      });
  }

  private loadSupplemental(): void {
    forkJoin({
      notifications: this.http
        .get<ApiPaginated<ApiNotification>>(`${this.api}/portal/notifications?per_page=30`)
        .pipe(catchError((err) => this.logFail('notifications', err, { data: [] }))),
      walletSummary: this.http
        .get<ApiEnvelope<ApiWalletSummary>>(`${this.api}/portal/wallet`)
        .pipe(catchError((err) => this.logFail('wallet', err, { data: null }))),
      walletLedgers: this.http
        .get<ApiPaginated<ApiWalletLedger>>(`${this.api}/portal/wallet/transactions`)
        .pipe(catchError((err) => this.logFail('wallet transactions', err, { data: [] }))),
      transactions: this.http
        .get<ApiPaginated<ApiPaymentEntry>>(`${this.api}/portal/transactions`)
        .pipe(catchError((err) => this.logFail('transactions', err, { data: [] }))),
      summary: this.http
        .get<ApiEnvelope<ApiTransactionSummary>>(`${this.api}/portal/transactions/summary`)
        .pipe(catchError((err) => this.logFail('transaction summary', err, { data: null }))),
    }).subscribe(({ notifications, walletSummary, walletLedgers, transactions, summary }) => {
      this.notifications.set((notifications.data ?? []).map(mapNotification));
      this.refreshUnreadCount();
      this.wallet.set(mapWallet(walletSummary.data, walletLedgers.data ?? []));
      this.transactions.set((transactions.data ?? []).map(mapTransaction));
      this.setTransactionSummary(summary.data);
    });
  }

  private setTransactionSummary(s: ApiTransactionSummary | null): void {
    const overview = s?.spending_overview ?? [];
    this.transactionSummary.set({
      totalSpent: Number(s?.total_spent ?? 0),
      spendingLabels: overview.map((o) => monthLabel(o.month)),
      spendingData: overview.map((o) => Number(o.total ?? 0)),
    });
  }

  refreshWallet(): void {
    forkJoin({
      summary: this.http
        .get<ApiEnvelope<ApiWalletSummary>>(`${this.api}/portal/wallet`)
        .pipe(catchError((err) => this.logFail('wallet', err, { data: null }))),
      ledgers: this.http
        .get<ApiPaginated<ApiWalletLedger>>(`${this.api}/portal/wallet/transactions`)
        .pipe(catchError((err) => this.logFail('wallet transactions', err, { data: [] }))),
    }).subscribe(({ summary, ledgers }) =>
      this.wallet.set(mapWallet(summary.data, ledgers.data ?? []))
    );
  }

  exportTransactions(status?: string): Observable<Blob> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get(`${this.api}/portal/transactions/export${query}`, {
      responseType: 'blob',
    });
  }

  fundWallet(amount: number): Observable<{ authorizationUrl: string; refId: string }> {
    return this.http
      .post<ApiEnvelope<{ authorization_url: string; reference: string }>>(
        `${this.api}/portal/wallet/fund`,
        { amount }
      )
      .pipe(
        map((res) => {
          const url = res.data?.authorization_url;
          if (!url) throw new Error('Could not start wallet funding.');
          return { authorizationUrl: url, refId: res.data.reference };
        })
      );
  }

  withdrawFromWallet(
    amount: number,
    bankAccountId: string
  ): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.api}/portal/wallet/withdraw`, {
        amount,
        bank_account_id: bankAccountId,
      })
      .pipe(map((res) => ({ message: res?.message ?? 'Withdrawal initiated.' })));
  }

  listBankAccounts(): Observable<BankAccount[]> {
    return this.http
      .get<ApiEnvelope<ApiBankAccount[]>>(`${this.api}/portal/bank-accounts`)
      .pipe(
        map((res) => (res.data ?? []).map(mapBankAccount)),
        catchError((err) => this.logFail('bank accounts', err, [] as BankAccount[]))
      );
  }

  listBanks(): Observable<Bank[]> {
    return this.http
      .get<{ data?: { name?: string; code?: string }[] | null } | { name?: string; code?: string }[]>(
        `${this.api}/portal/bank-accounts/banks`
      )
      .pipe(
        map((res) => {
          const raw = Array.isArray(res)
            ? res
            : Array.isArray(res?.data)
              ? res.data
              : [];
          return raw
            .filter((b): b is { name: string; code: string } => !!b?.name && !!b?.code)
            .map((b) => ({ name: b.name, code: String(b.code) }))
            .sort((a, b) => a.name.localeCompare(b.name));
        })
      );
  }

  resolveBankAccount(
    accountNumber: string,
    bankCode: string
  ): Observable<{ accountName: string }> {
    return this.http
      .post<{ data: { account_name: string } }>(
        `${this.api}/portal/bank-accounts/resolve`,
        { account_number: accountNumber, bank_code: bankCode }
      )
      .pipe(map((res) => ({ accountName: res.data?.account_name ?? '' })));
  }

  addBankAccount(payload: {
    accountNumber: string;
    bankName: string;
    bankCode: string;
    accountName: string;
  }): Observable<BankAccount> {
    return this.http
      .post<ApiEnvelope<ApiBankAccount>>(`${this.api}/portal/bank-accounts`, {
        account_number: payload.accountNumber,
        bank_name: payload.bankName,
        bank_code: payload.bankCode,
        account_name: payload.accountName,
      })
      .pipe(map((res) => mapBankAccount(res.data)));
  }

  markAllNotificationsRead(): void {
    this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
    this.serverUnreadCount.set(0);
    this.http
      .post(`${this.api}/portal/notifications/read-all`, {})
      .subscribe({
        error: (err) => console.error('Failed to mark notifications read', err),
      });
  }

  markNotificationRead(id: string): void {
    const target = this.notifications().find((n) => n.id === id);
    if (!target || target.read) return;

    this.notifications.update((list) =>
      list.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    this.serverUnreadCount.update((c) =>
      c == null ? null : Math.max(0, c - 1)
    );
    this.http
      .post(`${this.api}/portal/notifications/${id}/read`, {})
      .subscribe({
        error: (err) => console.error('Failed to mark notification read', err),
      });
  }

  refreshUnreadCount(): void {
    this.http
      .get<{ success?: boolean; count?: number }>(
        `${this.api}/portal/notifications/unread-count`
      )
      .subscribe({
        next: (res) => this.serverUnreadCount.set(Number(res.count ?? 0)),
        error: () => this.serverUnreadCount.set(null),
      });
  }

  // ─── Public (guest) endpoints — do not write into authenticated signals ─

  fetchPublicPublications(params?: {
    per_page?: number;
    search?: string;
    category_id?: string | number;
  }): Observable<Publication[]> {
    const qs = new URLSearchParams();
    if (params?.per_page != null) qs.set('per_page', String(params.per_page));
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    if (params?.category_id != null && params.category_id !== '') {
      qs.set('category_id', String(params.category_id));
    }
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return this.http
      .get<ApiPaginated<ApiPublication> | ApiPublication[]>(
        `${this.api}/core/public/publications${query}`
      )
      .pipe(
        map((res) => {
          const list = Array.isArray(res) ? res : (res.data ?? []);
          return list.map(mapPublication);
        }),
        catchError((err) => {
          console.error('Failed to load public publications', err);
          return of([]);
        })
      );
  }

  fetchPublicPublicationByReference(reference: string): Observable<Publication | null> {
    const ref = encodeURIComponent(reference.trim());
    return this.http
      .get<ApiEnvelope<ApiPublication> | ApiPublication>(
        `${this.api}/core/public/publications/reference/${ref}`
      )
      .pipe(
        map((res) => {
          const api =
            res && typeof res === 'object' && 'data' in res && (res as ApiEnvelope<ApiPublication>).data
              ? (res as ApiEnvelope<ApiPublication>).data
              : (res as ApiPublication);
          return api ? mapPublication(api) : null;
        }),
        catchError((err) => {
          if ((err as { status?: number })?.status === 404) {
            return of(null);
          }
          console.error('Failed to look up public publication', err);
          return of(null);
        })
      );
  }

  fetchPublicCategories(): Observable<Category[]> {
    return this.http
      .get<ApiEnvelope<ApiCategory[]> | ApiCategory[] | ApiPaginated<ApiCategory>>(
        `${this.api}/core/public/categories`
      )
      .pipe(
        map((res) => {
          const list = Array.isArray(res)
            ? res
            : Array.isArray((res as ApiEnvelope<ApiCategory[]>).data)
              ? (res as ApiEnvelope<ApiCategory[]>).data
              : ((res as ApiPaginated<ApiCategory>).data ?? []);
          return list.map(mapCategory);
        }),
        catchError((err) => {
          console.error('Failed to load public categories', err);
          return of([]);
        })
      );
  }

  fetchPublicNewspapers(): Observable<Newspaper[]> {
    return this.http
      .get<
        | ApiEnvelope<
            Array<{ id?: number | string; name?: string; email?: string; logo_url?: string | null }>
          >
        | Array<{ id?: number | string; name?: string; email?: string; logo_url?: string | null }>
      >(`${this.api}/core/public/newspapers`)
      .pipe(
        map((res) => {
          const list = Array.isArray(res) ? res : (res.data ?? []);
          return list.map((u, i) => {
            const paper = mapNewspaper({
              id: typeof u.id === 'number' ? u.id : Number(u.id) || i + 1,
              name: u.name ?? `Newspaper ${i + 1}`,
              logo_url: u.logo_url ?? null,
            });
            return u.email?.trim() ? { ...paper, email: u.email.trim() } : paper;
          });
        }),
        catchError((err) => {
          console.error('Failed to load public newspapers', err);
          return of([]);
        })
      );
  }

  /**
   * Authenticated verify — POST /portal/publications/verify.
   * Response shape: { message, data: { verified, publication } }.
   */
  verifyPublication(payload: {
    reference_number?: string;
    verification_code?: string;
    qr_code?: string;
  }): Observable<{ verified: boolean; publication: Publication | null }> {
    return this.http
      .post<{
        message?: string;
        data: { verified: boolean; publication: ApiPublication | null };
      }>(`${this.api}/portal/publications/verify`, payload)
      .pipe(
        map((res) => ({
          verified: !!res.data?.verified,
          publication: res.data?.publication
            ? mapPublication(res.data.publication)
            : null,
        })),
        catchError((err) => {
          const status = (err as { status?: number })?.status;
          if (status === 404) {
            return of({ verified: false, publication: null });
          }
          return throwError(() => err);
        })
      );
  }
}

function monthLabel(month: string): string {
  const [year, m] = (month ?? '').split('-');
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const name = names[Number(m) - 1];
  if (!name) return month ?? '';
  return year ? `${name} ${year.slice(2)}` : name;
}

/** @deprecated Use PortalDataService */
export {PortalDataService as MockDataService};

